import React from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {
  getAnonymousId,
  getIsRtlLanguage,
  getMyBookingsUrl
} from 'modules/meta/selectors'
import {OverlayType} from 'modules/overlay/types'
import {getIsFromKayak} from 'utils/trafficSource'

import {buttonSizesMap} from '@vio/atlas/Button'
import {ListItem} from '@vio/atlas/ListItem'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {Variant} from '@vio/atlas/src/components/designSystem/Button/Button'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {
  TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY,
  trackUserAuthClick
} from '@vio/core/src/auth/modules/actions'
import {
  getUser,
  selectIsAuthenticated,
  selectIsLoading
} from '@vio/core/src/auth/modules/selectors'
import {Connections} from '@vio/core/src/auth/modules/selectors.shared'
import {makeSocialLogin} from '@vio/core/src/auth/modules/thunks'
import {
  AuthenticationFlows,
  AuthenticationMethod,
  ShowPasswordlessLoginPayload,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
import {Placement} from '@vio/core/src/deepLinking/types'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {useLogIn} from '@vio/shared/src/authentication/hooks/useLogIn'
import {useLogOut} from '@vio/shared/src/authentication/hooks/useLogOut'
import {HeaderUserWidget} from '@vio/shared/src/user/account/HeaderUserWidget'
import {PointsProgramBadge} from '@vio/shared/src/whiteLabel/PointsProgramBadge'

const GetTheAppLink = styled.span`
  color: ${cssTheme.colors.content.brand.primary};
`

interface Props {
  /** Button variant */
  buttonVariant?: Variant
}

const InverseSkeletonLoader = styled(SkeletonLoader)`
  .react-loading-skeleton {
    --base-color: rgba(255, 255, 255, 0.24);
    --highlight-color: rgba(255, 255, 255, 0.4);
  }
`

const PlaceholderWrapper = styled.div`
  height: ${buttonSizesMap.md.size}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const UserWidget = ({buttonVariant}: Props) => {
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const logIn = useLogIn()
  const isLoading = useSelector(selectIsLoading)
  const user = useSelector(getUser)
  const logOut = useLogOut()
  const dispatch = useDispatch()
  const showOverlay = useShowOverlay()
  const {brandCode} = useBrand()
  const {
    auth: {hasOidcProvider},
    bookings: {hasMyBookings},
    retention: {hasGetTheApp}
  } = useBrandCapabilities()
  const anonymousId = useSelector(getAnonymousId)
  const myBookingsUrl = useSelector(getMyBookingsUrl)
  const isKayakTraffic = useSelector(getIsFromKayak)
  const isRtl = useSelector(getIsRtlLanguage)

  const handleOnSignIn = ({
    sourceComponent,
    flow
  }: {
    sourceComponent: ShowPasswordlessLoginPayload['sourceComponent']
    flow: AuthenticationFlows
  }) => {
    if (hasOidcProvider) {
      const analyticsData = {authenticationMethod: brandCode}

      dispatch(
        trackUserAuthClick({
          authenticationMethod: AuthenticationMethod.OIDCFederation,
          sourceComponent: SourceComponentType.Header,
          authenticationFlow: flow,
          analyticsData
        })
      )

      setTimeout(
        () =>
          dispatch(
            makeSocialLogin({
              connection: Connections.OIDCFederation,
              unlockHotelId: '',
              sourceComponent: SourceComponentType.Header,
              flow,
              analyticsData
            })
          ),
        TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY
      )
      return
    }

    setTimeout(
      () =>
        logIn({
          sourceComponent,
          anonymousId,
          flow
        }),
      TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY
    )
  }

  const openGetTheAppPopup = (event: React.MouseEvent) => {
    event.preventDefault()

    showOverlay({
      type: OverlayType.Web2AppPopup,
      params: {
        placement: Placement.MenuItem
      }
    })
  }

  if (isLoading) {
    const Placeholder =
      buttonVariant === 'ghostInverse' ? InverseSkeletonLoader : SkeletonLoader

    return (
      <PlaceholderWrapper>
        <Placeholder width={72} />
      </PlaceholderWrapper>
    )
  }

  return (
    <HeaderUserWidget
      accountUrl="/user/account"
      alertsUrl="/user/alerts"
      logOut={logOut}
      isAuthenticated={Boolean(isAuthenticated)}
      user={user}
      myBookingsUrl={hasMyBookings && myBookingsUrl}
      onSignIn={handleOnSignIn}
      programSlot={<PointsProgramBadge />}
      getTheAppSlot={
        hasGetTheApp && (
          <ListItem
            dataId="desktop_account_drop_down"
            onClick={openGetTheAppPopup}
            title={
              <GetTheAppLink>
                <FormattedMessageWrapper
                  id="appBanner.button"
                  defaultMessage="Get the app"
                />
              </GetTheAppLink>
            }
          />
        )
      }
      buttonVariant={buttonVariant}
      isSignInHidden={isKayakTraffic}
      isRtl={isRtl}
    />
  )
}

export default UserWidget
